import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { switchMap, take } from 'rxjs';
import { environment } from '../../environments/environment';

// Endpoints fronted by the AWS secure proxy. Requests to these carry the user's
// Ping access token as a bearer; the Lambda validates it (JWT authorizer) and
// injects the backend secret server-side. Empty endpoints (envs not yet
// migrated) are filtered out so we never match every request with `startsWith('')`.
const proxyEndpoints = (): string[] =>
  [
    environment.aws.mulesoftEndpoint,
    environment.aws.fulfillmentEndpoint,
    environment.aws.sinequaEndpoint,
    environment.aws.assistantEndpoint,
  ].filter((url): url is string => !!url);

export const pingAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const isProxyCall = proxyEndpoints().some((url) => req.url.startsWith(url));
  if (!isProxyCall) {
    return next(req);
  }

  // Endpoints served from the SPA's own CloudFront distribution by a Lambda
  // Function URL fronted by Origin Access Control. OAC overwrites the standard
  // Authorization header with its own SigV4 signature, so for these the Ping
  // bearer also rides in a header it does not touch (X-Ping-Authorization).
  //
  // Fulfillment is listed ahead of its own move to a Function URL (INEXP-2560):
  // the extra header is ignored by API Gateway, so sending it early lets the
  // SPA ship before the origin is repointed rather than in lockstep — repoint
  // first and every submission would 401. Mulesoft and sinequa stay on API
  // Gateway and keep working with Authorization alone.
  const oacEndpoints = [
    environment.aws.assistantEndpoint,
    environment.aws.fulfillmentEndpoint,
  ].filter((url): url is string => !!url);
  const isOac = oacEndpoints.some((url) => req.url.startsWith(url));

  const oidc = inject(OidcSecurityService);
  return oidc.getAccessToken().pipe(
    take(1),
    switchMap((token) => {
      if (!token) {
        return next(req);
      }
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      if (isOac) {
        headers['X-Ping-Authorization'] = `Bearer ${token}`;
      }
      return next(req.clone({ setHeaders: headers }));
    }),
  );
};
